import React, { useEffect, useState, useRef } from 'react';
import { Check, Plus, WandSparkles, Download, ChevronDown, FileText, FileType } from 'lucide-react';
import { GenerateButton, SubmitButton, SecondaryButton } from '@/components/ui/Button';
import SwitchButton from './SwitchButton';

export type ExportFormat = 'pdf' | 'word';

interface CompanyHeaderProps {
  title: React.ReactNode;
  description?: string;
  onGenerate?: () => void;
  onSave?: () => void;
  onPublish?: (published: boolean) => void;
  onCreate?: () => void;
  onExport?: (format: ExportFormat) => void;
  generateLabel?: string;
  saveLabel?: string;
  createLabel?: string;
  extraAction?: React.ReactNode;
  isSaving?: boolean;
  isPublished?: boolean;
  showExport?: boolean;
}

export default function CompanyHeader({
  title,
  description,
  onGenerate,
  onSave,
  onPublish,
  onCreate,
  onExport,
  generateLabel,
  saveLabel,
  createLabel,
  extraAction,
  isSaving,
  isPublished = false,
  showExport = false,
}: Readonly<CompanyHeaderProps>) {
  const [published, setPublished] = useState(isPublished);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPublished(isPublished);
  }, [isPublished]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setExportDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = (format: ExportFormat) => {
    onExport?.(format);
    setExportDropdownOpen(false);
  };

  const handlePublishToggle = (value: boolean) => {
    setPublished(value);
    onPublish?.(value);
  };

  return (
    <div className="p-5 flex items-center justify-between">
      <div className="flex flex-col">
        <h1 className="font-semibold text-lg">{title}</h1>
        {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
      </div>

      <div className="flex items-center space-x-3">
        {extraAction && <div className="mr-1">{extraAction}</div>}

        {onGenerate &&
          (generateLabel === 'Cancel' ? (
            <SecondaryButton onClick={onGenerate} className="flex items-center gap-1">
              {generateLabel}
            </SecondaryButton>
          ) : (
            <GenerateButton onClick={onGenerate} className="flex items-center gap-1">
              <WandSparkles size={16} />
              {generateLabel || 'Generate'}
            </GenerateButton>
          ))}

        {onPublish && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{published ? 'Published' : 'Publish All'}</span>
            <SwitchButton checked={published} onChange={handlePublishToggle} />
          </div>
        )}

        {showExport && onExport && (
          <div className="relative" ref={exportDropdownRef}>
            <SubmitButton
              onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
              className="flex items-center gap-1"
            >
              <Download size={16} />
              Export
              <ChevronDown size={16} className={`ml-1 transition-transform ${exportDropdownOpen ? 'rotate-180' : ''}`} />
            </SubmitButton>
            {exportDropdownOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                <button
                  onClick={() => handleExport('pdf')}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-md"
                >
                  <FileText size={16} className="text-red-500" />
                  Export as PDF
                </button>
                <button
                  onClick={() => handleExport('word')}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-b-md"
                >
                  <FileType size={16} className="text-blue-500" />
                  Export as Word
                </button>
              </div>
            )}
          </div>
        )}

        {onSave && (
          <SubmitButton onClick={onSave} disabled={isSaving} className="flex items-center gap-1">
            <Check size={16} />
            {isSaving ? 'Saving...' : saveLabel || 'Save'}
          </SubmitButton>
        )}

        {onCreate && (
          <SubmitButton onClick={onCreate} className="flex items-center gap-1">
            <Plus size={16} />
            {createLabel || 'Create'}
          </SubmitButton>
        )}
      </div>
    </div>
  );
}
